/**
 * Feedback API client (E13-7 — Brightmark Feedback Tool).
 *
 * Typed wrappers over the `/api/feedback/` endpoints, layered on the shared
 * `apiRequest` from `client.ts` so they inherit the LWP session + double-submit
 * CSRF behaviour (cookie credentials via `credentials: "include"`, the
 * `X-CSRFToken` echo on mutating calls, `X-Tenant`) and the `ApiError` status
 * semantics. There is NO Bearer token anywhere — the session lives in an
 * HttpOnly cookie.
 *
 * `submitFeedback` posts `multipart/form-data`. We hand `apiRequest` a raw
 * `FormData` body and rely on its FormData guard (it does NOT stamp a JSON
 * Content-Type onto FormData), so the browser sets its own
 * `multipart/form-data; boundary=…` and the boundary is preserved. The optional
 * annotation rides as a JSON string ONLY when present; the optional screenshot
 * / media file rides the single `media` file field.
 */
import { apiRequest } from "./client";
import type { BrightmarkAnnotation } from "../lib/brightmark";
import { snapshotTelemetry } from "../lib/brightmark/telemetry";

/** The page's entry starts the runtime-context ring buffers through here (src/v2 imports only src/api and src/mocks). */
export { installBrightmarkTelemetry } from "../lib/brightmark/telemetry";
/** The point-and-click picker (src/v2/app/pick) builds this; the same reason as above. */
export type { BrightmarkAnnotation };

/** The four report categories the backend `FeedbackCreateSerializer` accepts. */
export type FeedbackType = "bug" | "data_integrity" | "suggestion" | "feature";

/** One recorded API call in a report's runtime context: method + path + status ONLY (issue #587).
 * Never a request/response body, never a query string — see `lib/brightmark/telemetry.ts`. */
export interface ApiCallHint {
  method: string;
  path: string;
  status: number;
  ms?: number;
}

/** Whitelisted client-hint keys the backend keeps (everything else is dropped). */
export interface ClientHints {
  route?: string;
  page_name?: string;
  app_version?: string;
  viewport?: string;
  screen?: string;
  locale?: string;
  online?: boolean;
  platform?: string;
  user_agent?: string;
  console_errors?: string[];
  /** Recent API calls, oldest first (issue #587). */
  api_calls?: ApiCallHint[];
  /** The most recent API call at report time (issue #587). */
  last_api_call?: ApiCallHint;
  record_ref?: string;
  local_time?: string;
}

/** What became of a voice note's audio (#717). Only `transcribed` carries a transcript and a summary. */
export type VoiceNoteStatus = "transcribed" | "not_configured" | "ceiling" | "failed";

/** `POST /api/feedback/voice/` 201 body: the stored note, written out and summarised by the server when it can. */
export interface VoiceNoteResult {
  id: number;
  /** Seconds. */
  duration: number;
  status: VoiceNoteStatus;
  transcript: string;
  /** A few `- ` bullet points, suggested for the comment box. */
  summary: string;
  /** Why it was not written out, in plain words; "" when it was. */
  message: string;
}

export interface SubmitFeedbackInput {
  /** Required — one of the four categories. */
  type: FeedbackType;
  /** Required, non-blank (server caps at 5000). The only field the user must fill. */
  description: string;
  /** Appended as `JSON.stringify` ONLY when present; the key is omitted when null. */
  annotation?: BrightmarkAnnotation | null;
  /** Element crop screenshot, click marker burned in (from `dataUrlToBlob(shot.data_url)`) →
   * `media` field. */
  screenshot?: Blob | null;
  /** Dropzone upload / screen-recording webm → `media` field (wins over screenshot). */
  media?: Blob | File | null;
  /** Full-viewport screenshot captured alongside the crop → the SEPARATE `media_viewport` field
   * (issue #575 gap 2: "capture two images"). Only meaningful alongside `screenshot`; ignored when
   * a `media` file is present (a dropzone upload/recording has no viewport counterpart). */
  viewportScreenshot?: Blob | null;
  /** Optional — server derives a title from the description when omitted. */
  title?: string;
  route?: string;
  appVersion?: string;
  recordRef?: string;
  localTime?: string;
  clientHints?: ClientHints;
  /** Voice notes recorded in the dialog, by the ids `uploadVoiceNote` returned (#717): repeated `voice_notes`. */
  voiceNotes?: number[];
}

/** `POST /api/feedback/` 201 body. `message` is `"Thanks — logged as #NNN."`. */
export interface SubmitFeedbackResponse {
  id: number;
  status: string;
  issue_number: number | null;
  /** `github_issue_number || pk` — the human reference shown in the message. */
  reference: number;
  message: string;
}

/** One row in the agent-scoped report list. */
export interface FeedbackRow {
  id: number;
  feedback_type: FeedbackType | string;
  title: string;
  status: string;
  reference: number;
  created_at: string;
  is_done: boolean;
  closeout_summary: string | null;
  verify_url: string | null;
  /**
   * True when THIS report is the current viewer's own AND it resolved since
   * they last looked at 'My reports' (#578 — close the loop). Server-derived;
   * always `false` for someone else's row even in the admin queue.
   */
  unread: boolean;
}

/** Admin list rows add the GitHub-state + submitter fields. */
export interface AdminFeedbackRow extends FeedbackRow {
  github_issue_number: number | null;
  github_issue_url: string | null;
  submitted_by_id: number | null;
  submitted_by_name: string | null;
  issue_state: string | null;
  issue_closed_at: string | null;
  state_synced_at: string | null;
}

export interface FeedbackListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: (FeedbackRow | AdminFeedbackRow)[];
  completion: { done: number; total: number };
  /** Admin-only: the source filter options. */
  available_sources?: { value: string; label: string }[];
}

export interface FeedbackListQuery {
  type?: string;
  status?: string;
  completion?: "done" | "open";
  /** Admin-only. */
  source?: string;
  page?: number;
  page_size?: number;
}

/** Flat technical block on the report detail. */
export interface FeedbackTechnical {
  route: string | null;
  app_version: string | null;
  environment: string | null;
  user_agent: string | null;
  viewport: string | null;
  platform: string | null;
  locale: string | null;
  console_errors: string[] | null;
  /** Recent API calls at report time — method/path/status only (issue #587). */
  api_calls?: ApiCallHint[] | null;
  annotation: Record<string, unknown> | null;
}

export interface FeedbackDetail extends FeedbackRow {
  description: string;
  media: {
    kind: "image" | "video";
    stored_locally: boolean;
    url?: string;
    /** The full-viewport screenshot captured alongside this one (issue #575 gap 2), when present. */
    viewport?: {
      kind: "image" | "video";
      stored_locally: boolean;
      url?: string;
    };
  } | null;
  technical: FeedbackTechnical;
  /** Admin detail also carries the GitHub-state fields. */
  github_issue_number?: number | null;
  github_issue_url?: string | null;
  submitted_by_id?: number | null;
  submitted_by_name?: string | null;
  issue_state?: string | null;
  issue_closed_at?: string | null;
  state_synced_at?: string | null;
}

export interface FeedbackCloseoutInput {
  closeout_summary?: string;
  verify_url?: string;
}

export interface FeedbackSyncResult {
  updated: number;
  unchanged: number;
  skipped: number;
  scanned: number;
  note?: string;
  completion: { done: number; total: number };
}

/** `GET`/`POST /api/feedback/unread/` body — the close-the-loop signal (#578). */
export interface FeedbackUnreadResponse {
  /** How many of the viewer's OWN reports resolved since they last looked. */
  unread_count: number;
  last_seen_at: string | null;
}

/**
 * Fired on ``window`` whenever ``markFeedbackReportsSeen`` resolves, so every
 * mounted badge (e.g. the sidebar) can drop to 0 immediately rather than
 * waiting for its next poll.
 */
export const FEEDBACK_UNREAD_CHANGED_EVENT = "feedback:unread-changed";

/** Build a `?a=b&c=d` query string from defined params (skips undefined/empty). */
function buildQuery(
  params: Record<string, string | number | undefined>,
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== "") {
      search.set(key, String(value));
    }
  }
  const qs = search.toString();
  return qs ? `?${qs}` : "";
}

/**
 * `POST /api/feedback/` — multipart submit. Builds the `FormData` so the
 * boundary is browser-set (apiRequest does not stamp JSON onto FormData):
 *   - `type` + `description` always.
 *   - `title` / `route` / `app_version` / `record_ref` / `local_time` only when truthy.
 *   - `client_hints` as a JSON string when present.
 *   - `annotation` as a JSON string ONLY when non-null (key omitted otherwise).
 *   - a single `media` file: `input.media` (dropzone / recording) wins; else the
 *     `input.screenshot` Blob is sent as `media` named `screenshot.png`.
 *   - a SECOND, independent `media_viewport` file (issue #575 gap 2): only when `input.media` is
 *     absent (a dropzone upload / recording never has a viewport counterpart) and
 *     `input.viewportScreenshot` is present, sent named `viewport.png`.
 */
export function submitFeedback(
  input: SubmitFeedbackInput,
): Promise<SubmitFeedbackResponse> {
  const fd = new FormData();
  fd.append("type", input.type);
  fd.append("description", input.description);

  if (input.title) fd.append("title", input.title);
  if (input.route) fd.append("route", input.route);
  if (input.appVersion) fd.append("app_version", input.appVersion);
  if (input.recordRef) fd.append("record_ref", input.recordRef);
  if (input.localTime) fd.append("local_time", input.localTime);

  if (input.clientHints) {
    fd.append("client_hints", JSON.stringify(input.clientHints));
  }

  for (const id of input.voiceNotes ?? []) fd.append("voice_notes", String(id));

  // Annotation rides as a JSON string ONLY when present.
  if (input.annotation) {
    fd.append("annotation", JSON.stringify(input.annotation));
  }

  // One media slot: dropzone / recording wins over an annotation screenshot.
  if (input.media) {
    fd.append("media", input.media);
  } else if (input.screenshot) {
    fd.append("media", input.screenshot, "screenshot.png");
    // The viewport shot rides a second, independent field — only meaningful alongside the annotation
    // crop (a chosen file/recording has no viewport counterpart, so `input.media` above excludes it).
    if (input.viewportScreenshot) {
      fd.append("media_viewport", input.viewportScreenshot, "viewport.png");
    }
  }

  return apiRequest<SubmitFeedbackResponse>("/feedback/", {
    method: "POST",
    body: fd,
  });
}

/**
 * `POST /api/feedback/voice/` — one recorded voice note (#717), sent as soon as it is stopped. The server stores it with
 * the report's media, has it written out and summarised, and answers before the report is sent.
 */
export function uploadVoiceNote(audio: Blob, seconds: number): Promise<VoiceNoteResult> {
  const fd = new FormData();
  const ext = audio.type.includes("mp4") ? "m4a" : audio.type.includes("ogg") ? "ogg" : "webm";
  fd.append("audio", audio, `note.${ext}`);
  fd.append("duration", seconds.toFixed(1));
  return apiRequest<VoiceNoteResult>("/feedback/voice/", { method: "POST", body: fd });
}

/** `DELETE /api/feedback/voice/:id/` — the reporter deletes a note they have not sent. */
export function deleteVoiceNote(id: number): Promise<void> {
  return apiRequest<void>(`/feedback/voice/${id}/`, { method: "DELETE" });
}

/** `GET /api/feedback/` — role-scoped, paginated list with completion roll-up. */
export function listFeedbackReports(
  params: FeedbackListQuery = {},
): Promise<FeedbackListResponse> {
  const path = `/feedback/${buildQuery({
    type: params.type,
    status: params.status,
    completion: params.completion,
    source: params.source,
    page: params.page,
    page_size: params.page_size,
  })}`;
  return apiRequest<FeedbackListResponse>(path);
}

/** `GET /api/feedback/:id/` — role-scoped detail (404 when not visible). */
export function getFeedbackReport(
  id: number | string,
): Promise<FeedbackDetail> {
  return apiRequest<FeedbackDetail>(`/feedback/${id}/`);
}

/** `PATCH /api/feedback/:id/` — admin-only close-out (≥1 field required). */
export function updateFeedbackCloseout(
  id: number | string,
  body: FeedbackCloseoutInput,
): Promise<FeedbackDetail> {
  return apiRequest<FeedbackDetail>(`/feedback/${id}/`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

/** `POST /api/feedback/sync/` — admin-only GitHub-state reconcile. */
export function syncFeedbackIssueState(
  ids?: number[],
): Promise<FeedbackSyncResult> {
  return apiRequest<FeedbackSyncResult>("/feedback/sync/", {
    method: "POST",
    body: JSON.stringify(ids ? { ids } : {}),
  });
}

/**
 * `GET /api/feedback/unread/` — the current user's unread-resolution count
 * (#578). Server-side (not localStorage) — a reporter may use multiple
 * machines, so the "have I seen this resolve yet" marker must live server-side.
 */
export function getFeedbackUnreadCount(): Promise<FeedbackUnreadResponse> {
  return apiRequest<FeedbackUnreadResponse>("/feedback/unread/");
}

/**
 * `POST /api/feedback/unread/` — mark 'My reports' seen NOW, clearing the
 * badge for next time. Dispatches `FEEDBACK_UNREAD_CHANGED_EVENT` on success
 * so every mounted badge (e.g. the sidebar) updates immediately.
 */
export function markFeedbackReportsSeen(): Promise<FeedbackUnreadResponse> {
  return apiRequest<FeedbackUnreadResponse>("/feedback/unread/", {
    method: "POST",
    body: JSON.stringify({}),
  }).then((res) => {
    if (typeof window !== "undefined") {
      window.dispatchEvent(new Event(FEEDBACK_UNREAD_CHANGED_EVENT));
    }
    return res;
  });
}

/**
 * The query parameters a reported route keeps: what names the open sheet (an id; `SHEET_PARAMS` in
 * src/v2/app/oneSheet.ts) and the view. Anything else, a search box's `?q=` above all, may be a member's name, so it is
 * never sent (NDA, docs/operations/brightmark-feedback-triage.md). The server keeps the same list (`ROUTE_PARAMS` in
 * backend/apps/feedback/context.py) and drops the rest again.
 */
export const REPORTED_ROUTE_PARAMS: readonly string[] = ["person", "order", "panel", "run", "open", "report", "view", "campaign"];

/** The address a report names: the path and only the `REPORTED_ROUTE_PARAMS` of its query. */
export function reportedRoute(pathname: string, search: string): string {
  const kept = new URLSearchParams([...new URLSearchParams(search)].filter(([key]) => REPORTED_ROUTE_PARAMS.includes(key)));
  const query = kept.toString();
  return query ? `${pathname}?${query}` : pathname;
}

/**
 * The build identity of this bundle. Stamped at build time by `vite.config.ts`, which resolves the
 * git SHA of the checkout it builds from (issue #587) and falls back to `"unknown"` when git is
 * unavailable. `undefined` in dev/test (no `define` applied) — treated the same as `"unknown"` so a
 * report never claims a version it doesn't have.
 */
function resolveBuildVersion(): string | undefined {
  const raw = import.meta.env.VITE_APP_VERSION;
  if (typeof raw !== "string") return undefined;
  const trimmed = raw.trim();
  if (!trimmed || trimmed === "unknown") return undefined;
  return trimmed.slice(0, 64); // backend `app_version` caps at 64 chars.
}

/**
 * Collect client-side context for a report. Reads `window` / `navigator` /
 * `document` defensively (SSR / jsdom safe) and drains the Brightmark
 * console/API ring buffers (issue #587) so an "it doesn't work" report carries
 * the evidence of what actually failed. No PII assembly: the ring buffers redact
 * before draining, the server sanitises again, and `user_agent` is overwritten
 * server-authoritatively.
 */
export function captureClientHints(): ClientHints {
  const hints: ClientHints = {};
  const appVersion = resolveBuildVersion();
  if (appVersion) hints.app_version = appVersion;
  if (typeof window === "undefined") return hints;

  // Runtime context: last ~20 console messages + last ~10 API calls (method/path/status only).
  try {
    const telemetry = snapshotTelemetry();
    if (telemetry.console_errors.length > 0) {
      hints.console_errors = telemetry.console_errors;
    }
    if (telemetry.api_calls.length > 0) {
      hints.api_calls = telemetry.api_calls;
    }
    if (telemetry.last_api_call) {
      hints.last_api_call = telemetry.last_api_call;
    }
  } catch {
    // best-effort — a report without runtime context is still worth sending
  }

  try {
    // The path and the open record (the person sheet's `?person=<id>`, issue #497), never the rest of the query (#717).
    hints.route = reportedRoute(window.location?.pathname ?? "", window.location?.search ?? "");
    hints.viewport = `${window.innerWidth}x${window.innerHeight}`;
    if (typeof window.screen !== "undefined") {
      hints.screen = `${window.screen.width}x${window.screen.height}`;
    }
    hints.local_time = new Date().toISOString();
  } catch {
    // best-effort; partial hints are fine
  }

  if (typeof navigator !== "undefined") {
    try {
      hints.locale = navigator.language;
      hints.online = navigator.onLine;
      // navigator.platform is deprecated but still widely populated.
      hints.platform = (navigator as Navigator & { platform?: string }).platform;
      hints.user_agent = navigator.userAgent;
    } catch {
      // best-effort
    }
  }

  return hints;
}
